import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { featureTierService } from '@/services/featureTierService'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Require internal access (rp_admin or rp_support) */
  requireInternal?: boolean
  /** Redirect path if access denied (defaults to '/') */
  fallbackPath?: string
  /** If true, preserve query params when redirecting */
  preserveQuery?: boolean
}

/**
 * Route guard component for role-based access control.
 *
 * Usage:
 * <Route path="/rp-admin/feedback" element={
 *   <ProtectedRoute requireInternal fallbackPath="/feedback" preserveQuery>
 *     <FeedbackManagementPage />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({
  children,
  requireInternal = false,
  fallbackPath = '/',
  preserveQuery = false
}: ProtectedRouteProps) {
  const { user } = useAuth()
  const location = useLocation()

  // Check access based on requirements
  if (requireInternal && !featureTierService.hasInternalAccess(user)) {
    // Build redirect path
    let redirectTo = fallbackPath
    if (preserveQuery && location.search) {
      redirectTo = `${fallbackPath}${location.search}`
    }

    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
